import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { Search, Gift, Check, Clock, XCircle, Eye, Mail, AlertTriangle } from "lucide-react";

interface GiftVoucher {
  id: string;
  code: string;
  amount: number;
  currency: string;
  buyerName: string;
  buyerEmail: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  designTemplate: string;
  status: "active" | "used" | "expired";
  usedAt?: string;
  expiresAt: string;
  pdfUrl?: string;
  sentAt?: string;
  createdAt: string;
}

interface VouchersResponse {
  data: GiftVoucher[];
  stats: {
    total: number;
    active: number;
    used: number;
    expired: number;
    totalValue: number;
    activeValue: number;
  };
}

export default function VouchersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["vouchers", statusFilter],
    queryFn: () =>
      api.get<ApiResponse<GiftVoucher[]> & { stats: VouchersResponse["stats"] }>(
        `/api/admin/vouchers?status=${statusFilter}`,
      ),
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/vouchers/${id}/resend`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
    },
  });

  const vouchers = data?.data || [];
  const stats = data?.stats;

  // Filter vouchers by search
  const filteredVouchers = vouchers.filter((voucher) => {
    const matchesSearch =
      voucher.code.toLowerCase().includes(search.toLowerCase()) ||
      voucher.buyerName.toLowerCase().includes(search.toLowerCase()) ||
      voucher.buyerEmail.toLowerCase().includes(search.toLowerCase()) ||
      voucher.recipientName?.toLowerCase().includes(search.toLowerCase()) ||
      voucher.recipientEmail?.toLowerCase().includes(search.toLowerCase());

    return matchesSearch;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
            <Check className="mr-1 h-3 w-3" />
            Active
          </span>
        );
      case "used":
        return (
          <span className="badge bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            <XCircle className="mr-1 h-3 w-3" />
            Used
          </span>
        );
      case "expired":
        return (
          <span className="badge bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400">
            <Clock className="mr-1 h-3 w-3" />
            Expired
          </span>
        );
      default:
        return null;
    }
  };

  const isExpiringSoon = (expiresAt: string) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(expiresAt) <= thirtyDaysFromNow && new Date(expiresAt) > new Date();
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (error) {
    return (
      <div className="card text-error-600 text-center">
        Error loading vouchers. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gift Vouchers</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage gift vouchers</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="bg-primary-100 dark:bg-primary-900/30 rounded-full p-2">
                <Gift className="text-primary-600 dark:text-primary-400 h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="bg-success-100 dark:bg-success-900/30 rounded-full p-2">
                <Check className="text-success-600 dark:text-success-400 h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-gray-100 p-2 dark:bg-gray-700">
                <XCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Used</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.used}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="bg-accent-100 dark:bg-accent-900/30 rounded-full p-2">
                <Gift className="text-accent-600 dark:text-accent-400 h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Value</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(stats.activeValue, "CZK")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, name, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <div className="flex gap-2">
            {["all", "active", "used", "expired"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`btn-sm capitalize ${statusFilter === status ? "btn-primary" : "btn-ghost"}`}
              >
                {status === "all" ? "All" : status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        ) : filteredVouchers.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <Gift className="mb-2 h-12 w-12 text-gray-300" />
            <p>No vouchers found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Code
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Buyer
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Recipient
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Expires
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-6 py-4">
                    <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm dark:bg-gray-700">
                      {voucher.code}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(voucher.amount, voucher.currency)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {voucher.buyerName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {voucher.buyerEmail}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {voucher.recipientName || voucher.recipientEmail ? (
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {voucher.recipientName || "-"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {voucher.recipientEmail || "-"}
                        </p>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(voucher.status)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 dark:text-gray-300">
                        {new Date(voucher.expiresAt).toLocaleDateString("en-US")}
                      </span>
                      {voucher.status === "active" && isExpiringSoon(voucher.expiresAt) && (
                        <span title="Expiring soon">
                          <AlertTriangle className="text-warning-500 h-4 w-4" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/vouchers/${voucher.id}`}
                        className="btn-ghost btn-sm"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      {voucher.status === "active" && (
                        <button
                          onClick={() => resendMutation.mutate(voucher.id)}
                          disabled={resendMutation.isPending}
                          className="btn-ghost btn-sm text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                          title="Resend email"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
